import { useLocation } from "wouter"
import { AppContainer } from "@/components/layout/app-container"
import { PersonProfileCard } from "@/pages/contrato/components/person-profile-card"
import { Button } from "@/components/ui/button"
import { PageLoading } from "../components/page-loading"
import { PageError } from "../components/page-error"
import { useContratoDetails } from "../hooks/use-contrato-details"
import { BackButton } from "../components/contrato-details/back-button"

export function ContratoDetails() {
  const [, navigate] = useLocation()

  const { formateDateUtcToLocal, personProfile, contract, isLoading, error, contractId } = useContratoDetails()

  if (isLoading) {
    return <PageLoading title="Contrato Individual" layout="details" />
  }

  if (error || !contract) {
    return (
      <PageError
        title="Contrato Não Encontrado"
        error={error}
        entityName="Contrato"
        entityId={contractId}
        backUrl="/contratual/contratos"
        backLabel="Voltar para Contratos"
      />
    )
  }

  return (
    <AppContainer title="Contrato Individual" description="">
      <BackButton name={personProfile.name} role={personProfile.role} />

      <div className="flex gap-[32px]">
        <PersonProfileCard person={personProfile} />

        {/* Main Content */}
        <div className="flex-1 bg-[#15203c] rounded-[20px] p-[30px] flex flex-col gap-[29px]">
          {/* Contract Info Section */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Informações do Contrato</h3>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Status</p>
                <p className="text-white font-medium">{contract?.data.contractStatus}</p>
              </div>

              <div>
                <p className="text-sm text-gray-400">Ativo</p>
                <p className="text-white font-medium">{contract?.data.active ? "Sim" : "Não"}</p>
              </div>

              <div>
                <p className="text-sm text-gray-400">Data de Início</p>
                <p className="text-white font-medium">
                  Não informada
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-400">Data de Término</p>
                <p className="text-white font-medium">
                  {formateDateUtcToLocal(contract?.data.contractTill)}
                </p>
              </div>

              {contract?.data.signedAt && (
                <div>
                  <p className="text-sm text-gray-400">Data de Assinatura</p>
                  <p className="text-white font-medium">
                    {formateDateUtcToLocal(contract.data.signedAt)}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-400">Criado em</p>
                <p className="text-white font-medium">
                  {formateDateUtcToLocal(contract?.data.createdAt)}
                </p>
              </div>
            </div>

            {/* Financial Info */}
            <div>
              <h4 className="text-md font-semibold text-white mb-3">Valores</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Valor Total</p>
                  <p className="text-white font-medium">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(contract?.data.totalAmount)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-400">Valor Mensal</p>
                  <p className="text-white font-medium">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(contract?.data.monthAmount)}
                  </p>
                </div>

                {contract?.data.amountPaidTillNow > 0 && (
                  <div>
                    <p className="text-sm text-gray-400">Valor Pago até Agora</p>
                    <p className="text-white font-medium">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(contract.data.amountPaidTillNow)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Copyright Info */}
            {contract?.data.hasCopyright && (
              <div>
                <h4 className="text-md font-semibold text-white mb-3">Direitos Autorais</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Possui Direitos</p>
                    <p className="text-white font-medium">Sim</p>
                  </div>

                  {contract?.data.copyrightType && (
                    <div>
                      <p className="text-sm text-gray-400">Tipo de Direito</p>
                      <p className="text-white font-medium">{contract.data.copyrightType}</p>
                    </div>
                  )}

                  {contract?.data.rlpParticipationPercent > 0 && (
                    <div>
                      <p className="text-sm text-gray-400">% RLP</p>
                      <p className="text-white font-medium">{contract.data.rlpParticipationPercent}%</p>
                    </div>
                  )}

                  {contract?.data.creditsAttribution && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-400">Atribuição de Créditos</p>
                      <p className="text-white font-medium">{contract.data.creditsAttribution}</p>
                    </div>
                  )}

                  {contract?.data.bibliotecaNacionalReg && (
                    <div>
                      <p className="text-sm text-gray-400">Registro Biblioteca Nacional</p>
                      <p className="text-white font-medium">{contract.data.bibliotecaNacionalReg}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ANCINE/Tax Info */}
            {(contract?.data.cnaeCodes || contract?.data.serviceCode || contract?.data.taxRegime || contract?.data.drtNumber || contract?.data.caneRegistration) && (
              <div>
                <h4 className="text-md font-semibold text-white mb-3">Informações Fiscais e ANCINE</h4>
                <div className="grid grid-cols-2 gap-4">
                  {contract?.data.cnaeCodes && (
                    <div>
                      <p className="text-sm text-gray-400">Códigos CNAE</p>
                      <p className="text-white font-medium">{contract.data.cnaeCodes}</p>
                    </div>
                  )}

                  {contract?.data.serviceCode && (
                    <div>
                      <p className="text-sm text-gray-400">Código de Serviço</p>
                      <p className="text-white font-medium">{contract.data.serviceCode}</p>
                    </div>
                  )}

                  {contract?.data.taxRegime && (
                    <div>
                      <p className="text-sm text-gray-400">Regime Tributário</p>
                      <p className="text-white font-medium">{contract.data.taxRegime}</p>
                    </div>
                  )}

                  {contract?.data.drtNumber && (
                    <div>
                      <p className="text-sm text-gray-400">Número DRT</p>
                      <p className="text-white font-medium">{contract.data.drtNumber}</p>
                    </div>
                  )}

                  {contract?.data.caneRegistration && (
                    <div>
                      <p className="text-sm text-gray-400">Registro CANE</p>
                      <p className="text-white font-medium">{contract.data.caneRegistration}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <p className="text-sm text-gray-400 mb-2">Descrição</p>
              <p className="text-sm text-white whitespace-pre-wrap">{contract?.data.contractDescription}</p>
            </div>

            {/* Witnesses */}
            {(contract?.data.testmentOneId || contract?.data.testmentTwoId) && (
              <div>
                <h4 className="text-md font-semibold text-white mb-3">Testemunhas</h4>
                <div className="grid grid-cols-2 gap-4">
                  {contract?.data.testmentOneId && (
                    <div>
                      <p className="text-sm text-gray-400">Testemunha 1</p>
                      <p className="text-white font-medium">ID: {contract.data.testmentOneId}</p>
                    </div>
                  )}

                  {contract?.data.testmentTwoId && (
                    <div>
                      <p className="text-sm text-gray-400">Testemunha 2</p>
                      <p className="text-white font-medium">ID: {contract.data.testmentTwoId}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Budget Line Item */}
            {contract?.data.budgetLineItemId && (
              <div>
                <p className="text-sm text-gray-400">Item da Linha Orçamentária</p>
                <p className="text-white font-medium">ID: {contract.data.budgetLineItemId}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-[10px] pt-4 border-t border-gray-700">
            {contract?.data.contractStatus === "DRAFT" && (
              <Button
                onClick={() => navigate(`/contratual/contratos/${contract?.data.id}/editar`)}
                className="bg-[#7846ff] hover:bg-[#7846ff]/90"
              >
                Editar Contrato
              </Button>
            )}

            {contract?.data.uploadedContractUrl && (
              <Button
                variant="outline"
                onClick={() => window.open(contract?.data.uploadedContractUrl!, '_blank')}
              >
                Download PDF
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppContainer>
  )
}
